import { useMemo, useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import ListItemIcon from "@mui/material/ListItemIcon";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import packageJson from "../../../package.json";
import VuiTypography from "components/VuiTypography";
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";
import { signOut as firebaseSignOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import { useAuthz } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import { apiRequest } from "services/tenantUserAdminService";
import NotificationItem from "examples/Items/NotificationItem";

const { gradients, white, info, borderCol, transparent } = colors;

function isRouteActive(pathname, route) {
  if (!route) return false;
  return pathname === route || pathname.startsWith(`${route}/`);
}

function isParentActive(pathname, route) {
  if (route.route && isRouteActive(pathname, route.route)) return true;
  if (route.collapse) {
    return route.collapse.some((child) => isParentActive(pathname, child));
  }
  return false;
}

function findActiveChildName(pathname, route) {
  if (!route.collapse) return null;
  for (const child of route.collapse) {
    if (child.route && isRouteActive(pathname, child.route)) return child.name;
    if (child.collapse) {
      const found = findActiveChildName(pathname, child);
      if (found) return found;
    }
  }
  return null;
}

function collectVisibleChildren(items, canAccessRoute) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => {
      if (!item || item.type === "hidden" || item.type === "divider" || item.type === "title") return false;
      if (item.collapse) return true;
      return canAccessRoute(item);
    })
    .map((item) => ({
      ...item,
      collapse: item.collapse ? collectVisibleChildren(item.collapse, canAccessRoute) : null,
    }))
    .filter((item) => (item.collapse ? item.collapse.length > 0 : true));
}

function renderMenuItems(items, onNavigate, pathname, depth = 0) {
  return items.map((item) => {
    if (item.collapse && item.collapse.length > 0) {
      return (
        <Box key={item.key || item.name}>
          <MenuItem disabled sx={{ opacity: 1, pl: 2 + depth * 2, py: 1 }}>
            <VuiTypography variant="button" sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#fff" }}>
              {item.name}
            </VuiTypography>
          </MenuItem>
          {renderMenuItems(item.collapse, onNavigate, pathname, depth + 1)}
        </Box>
      );
    }

    const isActive = item.route && pathname === item.route;
    return (
      <MenuItem
        key={item.key || item.name}
        onClick={() => onNavigate(item.route || item.href)}
        sx={{ pl: 2 + depth * 2, py: 1, backgroundColor: isActive ? "rgba(255,255,255,0.12)" : "transparent", borderLeft: isActive ? "3px solid #ffffff" : "3px solid transparent" }}
      >
        {item.icon && <Box component="span" sx={{ display: "inline-flex", mr: 1 }}>{item.icon}</Box>}
        <VuiTypography variant="button" sx={{ fontSize: "0.78rem", fontWeight: isActive ? 700 : 600, color: "#fff" }}>
          {item.name}
        </VuiTypography>
      </MenuItem>
    );
  });
}

export default function TopMenuBar({
  routes,
  canAccessRoute,
  topOffset = 0,
  leftOffset = 0,
  rightOffset = 0,
}) {
  const history = useHistory();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const { isSuperAdmin, tenantId } = useAuthz();
  const { selectedCompanyId } = useCompany() || {};

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setIsAuthenticated(!!user));
    return () => unsub();
  }, []);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openNotifMenu, setOpenNotifMenu] = useState(null);

  const loadNotifications = useCallback(async () => {
    const tid = isSuperAdmin ? selectedCompanyId : tenantId;
    if (!tid) return;
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tid)}/notifications`,
        { query: { unreadOnly: "false" } }
      );
      const list = Array.isArray(data?.notifications) ? data.notifications : [];
      list.sort((a, b) => {
        if (a.read !== b.read) return a.read ? 1 : -1;
        return (b.createdAt || b.detectedAt || "").localeCompare(a.createdAt || a.detectedAt || "");
      });
      // Suppress director and PSC appointment notifications from the UI as requested
      const filtered = list.filter((n) => {
        const change = n.changeType || n.change_type || "";
        // Also exclude notifications dismissed for this user (backend sets _dismissedForUser)
        const dismissed = !!n._dismissedForUser;
        return !dismissed && change !== "director_appointed" && change !== "psc_appointed";
      });
      setNotifications(filtered.slice(0, 20));
      setUnreadCount(filtered.filter((n) => !n.read && !n._dismissedForUser).length);
    } catch (_) {}
  }, [tenantId, selectedCompanyId, isSuperAdmin]);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const handleOpenNotif = async (event) => {
    setOpenNotifMenu(event.currentTarget);
    const tid = isSuperAdmin ? selectedCompanyId : tenantId;
    if (tid && unreadCount > 0) {
      const unread = notifications.filter((n) => !n.read);
      await Promise.allSettled(
        unread.map((n) =>
          apiRequest(
            `/tenant/${encodeURIComponent(tid)}/notifications/${encodeURIComponent(n.id)}/read`,
            { method: "PATCH" }
          ).catch(() => {})
        )
      );
      // Refresh notification list after marking read so UI reflects server state
      try {
        await loadNotifications();
      } catch (_) {}
    }
  };
  const handleCloseNotif = () => setOpenNotifMenu(null);

  const handleSignOut = async () => {
    await firebaseSignOut(auth);
    history.push("/authentication/sign-in");
  };

  const menuRoutes = useMemo(
    () => collectVisibleChildren(routes, canAccessRoute).filter((route) => route.type !== "hidden"),
    [routes, canAccessRoute]
  );

  const handleOpen = (event, key) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(key);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const handleNavigate = (target) => {
    if (!target) return;
    if (/^https?:\/\//i.test(target)) {
      window.location.href = target;
    } else {
      history.push(target);
    }
    handleClose();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: `${topOffset}px`,
        left: 0,
        right: 0,
        width: "100%",
        background: linearGradient(gradients.menu.main, gradients.menu.state, gradients.menu.deg),
        backdropFilter: "blur(18px)",
        borderBottom: `1px solid ${borderCol.navbar}`,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
        zIndex: 1201,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.18)",
      }}
    >
      <Toolbar disableGutters sx={{ minHeight: 76, pl: 0, pr: 2, display: "flex", alignItems: "center", gap: 1 }}>
        {/* ── Left: Logo ── */}
        <Tooltip title={`LedgerHub Ltd Version: ${packageJson.version}`} arrow>
        <Box
          sx={{ display: "flex", alignItems: "center", flexShrink: 0, mr: 2, cursor: "pointer", backgroundColor: "#fff", borderRadius: 0, px: 2, py: 0, height: "100%", gap: 1.5 }}
          onClick={() => handleNavigate("/dashboard")}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="LedgerHub"
            sx={{ height: "100%", maxHeight: 64, objectFit: "contain" }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
            <Box component="span" sx={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>LedgerHub Ltd</Box>
            <Box component="span" sx={{ fontSize: "0.7rem", color: "#64748b" }}>Accounting Services</Box>
          </Box>
        </Box>
        </Tooltip>

        {/* ── Hamburger: visible on md and below ── */}
        <IconButton
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation menu"
          sx={{ display: { xs: "inline-flex", lg: "none" }, color: "#fff", ml: 1 }}
        >
          <Icon aria-hidden="true">menu</Icon>
        </IconButton>

        {/* ── Middle: Menu items (hidden on mobile/tablet) ── */}
        <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 0.5, overflowX: "auto", flexGrow: 1, flexWrap: "nowrap", whiteSpace: "nowrap", alignItems: "center" }}>
        {menuRoutes.map((route) => {
          if (route.collapse && route.collapse.length > 0) {
            const open = activeMenu === route.key;
            return (
              <Box key={route.key} sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Button
                  color="inherit"
                  onClick={(event) => handleOpen(event, route.key)}
                  endIcon={<Icon fontSize="small">expand_more</Icon>}
                  sx={{
                    color: isParentActive(pathname, route) || activeMenu === route.key ? white.main : "rgba(255,255,255,0.7)",
                    fontWeight: isParentActive(pathname, route) ? 700 : 500,
                    textTransform: "none",
                    borderRadius: 0,
                    px: 1.5,
                    backgroundColor: transparent.main,
                    borderBottom: isParentActive(pathname, route) ? `3px solid #ffffff` : "3px solid transparent",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.06)",
                      color: white.main,
                    },
                    "&:active, &:focus": {
                      color: white.main,
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                  }}
                >
                  {route.name}
                </Button>
                {isParentActive(pathname, route) && findActiveChildName(pathname, route) && (
                  <Box sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.6)", mt: 0.25, mb: 0.25 }}>
                    {findActiveChildName(pathname, route)}
                  </Box>
                )}
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  sx={{
                    "& .MuiPaper-root": {
                      background: "#0f1535",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: "10px",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                      mt: 1,
                    },
                    "& .MuiMenuItem-root": {
                      color: "#fff",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", color: "#fff" },
                    },
                  }}
                >
                  {renderMenuItems(route.collapse, handleNavigate, pathname)}
                </Menu>
              </Box>
            );
          }

          return (
            <Button
              key={route.key}
              color="inherit"
              onClick={() => handleNavigate(route.route || route.href)}
              sx={{
                flexShrink: 0,
              color: isRouteActive(pathname, route.route) ? white.main : "rgba(255,255,255,0.7)",
                fontWeight: isRouteActive(pathname, route.route) ? 700 : 500,
                textTransform: "none",
                borderRadius: 0,
              px: 1.5,
              borderBottom: isRouteActive(pathname, route.route) ? `3px solid #ffffff` : "3px solid transparent",
              backgroundColor: transparent.main,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.06)",
                color: white.main,
              },
              "&:active, &:focus": {
                color: white.main,
                backgroundColor: "transparent",
                boxShadow: "none",
              },
            }}
            >
            {route.name}
            </Button>
          );
        })}
        </Box>

        {/* ── Spacer for mobile ── */}
        <Box sx={{ flexGrow: 1, display: { xs: "flex", lg: "none" } }} />

        {/* ── Right: Notifications + Sign-out ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: 2 }}>
          <IconButton
            size="small"
            onClick={handleOpenNotif}
            aria-label="Open notifications"
            sx={{
              p: "7px",
              borderRadius: "8px",
              backgroundColor: "rgba(255,255,255,0.12)",
              border: "1.5px solid rgba(255,255,255,0.3)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.22)", borderColor: "rgba(255,255,255,0.6)" },
            }}
          >
            <Badge
              badgeContent={unreadCount || 0}
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: 10, minWidth: 16, height: 16,
                  backgroundColor: "#ffa726", color: "#000", fontWeight: 700,
                },
              }}
            >
              <Icon style={{ fontSize: "1.3rem", color: (unreadCount ? "#fff" : "rgba(255,255,255,0.65)") }}>notifications</Icon>
            </Badge>
          </IconButton>
          <Menu
            anchorEl={openNotifMenu}
            open={Boolean(openNotifMenu)}
            onClose={handleCloseNotif}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              mt: 1,
              "& .MuiPaper-root": {
                minWidth: 320,
                maxHeight: 420,
                overflowY: "auto",
                backgroundColor: "#0f1620",
                backdropFilter: "none",
                boxShadow: "0 8px 30px rgba(0,0,0,0.55)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#e7f2ff",
              },
              "& .MuiPaper-root *": {
                color: "#e7f2ff !important",
              },

            }}
          >
            {notifications.length === 0 ? (
              <NotificationItem
                color="text"
                image={<Icon fontSize="small" sx={{ color: "rgba(255,255,255,0.65)" }}>notifications_none</Icon>}
                title={["", "No notifications yet"]}
                date=""
                onClick={handleCloseNotif}
              />
            ) : (
              notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  color={n.read ? "text" : "info"}
                  image={<Icon fontSize="small" sx={{ color: "#fff" }}>notifications</Icon>}
                  title={[n.clientName || "", n.message || n.changeType || "Notification"]}
                  date=""
                  onClick={async () => {
                    handleCloseNotif();
                    const tid = isSuperAdmin ? selectedCompanyId : tenantId;
                    if (tid) {
                      try {
                        await apiRequest(`/tenant/${encodeURIComponent(tid)}/notifications/${encodeURIComponent(n.id)}`, { method: "DELETE" });
                      } catch (_) {}
                      try { await loadNotifications(); } catch (_) {}
                    }

                    const clientId = n.clientId || n.client_id || null;
                    const type = n.type || "";
                    const change = n.changeType || n.change_type || "";
                    let route;
                    if (type === "inbound_email") {
                      route = clientId ? { pathname: `/client-profiles/${encodeURIComponent(clientId)}`, state: { tab: 4 } } : "/client-profiles";
                    } else if (type === "director_change" || ["director_appointed","director_resigned","director_changed"].includes(change)) {
                      route = clientId ? `/directors?clientId=${encodeURIComponent(clientId)}` : "/directors";
                    } else if (["psc_appointed","psc_ceased","psc_changed"].includes(change)) {
                      route = "/client-profiles";
                    } else if (["company_name_changed","company_status_changed","address_changed","sic_codes_changed","accounts_dates_changed","confirmation_statement_changed"].includes(change)) {
                      route = "/client-profiles";
                    } else if (["consent_expiring","consent_expired","bank_feed","consent"].includes(type)) {
                      route = "/bank-feeds";
                    } else if (["exception","transaction"].includes(type)) {
                      route = "/exceptions";
                    } else if (type === "vat") {
                      route = "/vat-return";
                    } else if (type === "period_close") {
                      route = "/period-close";
                    } else if (type === "ch_change") {
                      route = "/client-profiles";
                    } else {
                      route = "/dashboard";
                    }
                    if (typeof route === 'object') history.push(route.pathname, route.state); else history.push(route);
                  }}
                  onDelete={async () => {
                    // Only delete the notification itself (do not affect any client/resource)
                    // Keep the notifications popup open while deleting
                    const tid = isSuperAdmin ? selectedCompanyId : tenantId;
                    if (!tid) return;
                    try {
                      await apiRequest(`/tenant/${encodeURIComponent(tid)}/notifications/${encodeURIComponent(n.id)}`, { method: "DELETE" });
                      setNotifications(prev => prev.filter(x => x.id !== n.id));
                      setUnreadCount(prev => Math.max(0, prev - (n.read ? 0 : 1)));
                    } catch (_) {
                      // ignore
                    }
                  }}
                />
              ))
            )}
          </Menu>

          {isAuthenticated ? (
            <>
              <IconButton
                onClick={(e) => setProfileAnchor(e.currentTarget)}
                aria-label="Open user menu"
                size="small"
                sx={{ p: "4px" }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "0.85rem",
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    border: "1.5px solid rgba(255,255,255,0.5)",
                  }}
                >
                  {auth.currentUser?.email?.[0]?.toUpperCase() || "U"}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={profileAnchor}
                open={Boolean(profileAnchor)}
                onClose={() => setProfileAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ mt: 1, "& .MuiPaper-root": { backgroundColor: "#1a2332" }, "& .MuiMenuItem-root": { fontSize: "0.9rem", py: 1.2, px: 2, color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } } }}
              >
                <MenuItem disabled sx={{ opacity: 1 }}>
                  <VuiTypography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: "0.85rem" }}>
                    {auth.currentUser?.email || ""}
                  </VuiTypography>
                </MenuItem>
                <Divider sx={{ my: 0.5, borderColor: "rgba(255,255,255,0.15)" }} />
                <MenuItem onClick={() => { setProfileAnchor(null); handleNavigate("/change-password"); }}>
                  <ListItemIcon><Icon fontSize="small" sx={{ color: "#fff" }}>lock</Icon></ListItemIcon>
                  <span style={{ color: "#fff", fontWeight: 500 }}>Change Password</span>
                </MenuItem>
                <MenuItem onClick={() => { setProfileAnchor(null); handleSignOut(); }}>
                  <ListItemIcon><Icon fontSize="small" sx={{ color: "#ff6b6b" }}>logout</Icon></ListItemIcon>
                  <span style={{ color: "#ff6b6b", fontWeight: 500 }}>Sign Out</span>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <IconButton
              size="small"
              onClick={() => handleNavigate("/authentication/sign-in")}
              aria-label="Sign in"
              sx={{ color: "#fff" }}
            >
              <Icon aria-hidden="true" style={{ fontSize: "1.3rem" }}>account_circle</Icon>
            </IconButton>
          )}
        </Box>
      </Toolbar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            backgroundColor: "#fff",
            pt: 2,
          },
        }}
      >
        <Box sx={{ px: 2, pb: 2, display: "flex", alignItems: "center" }}>
          <Box component="img" src="/logo.png" alt="LedgerHub" sx={{ height: 40, objectFit: "contain" }} />
        </Box>
        <Divider />
        <List sx={{ px: 1, pt: 1 }}>
          {menuRoutes.map((route) => {
            if (route.collapse && route.collapse.length > 0) {
              const expanded = mobileExpanded === route.key;
              return (
                <Box key={route.key}>
                  <ListItemButton
                    onClick={() => setMobileExpanded(expanded ? null : route.key)}
                    sx={{
                      borderRadius: 1.5,
                      mb: 0.5,
                      color: isParentActive(pathname, route) ? "#1e3a5f" : "#475569",
                      fontWeight: isParentActive(pathname, route) ? 700 : 500,
                      backgroundColor: isParentActive(pathname, route) ? "#eff6ff" : "transparent",
                    }}
                  >
                    <ListItemText primary={route.name} primaryTypographyProps={{ sx: { fontSize: "0.875rem", fontWeight: "inherit" } }} />
                    <Icon sx={{ fontSize: "1.2rem" }}>{expanded ? "expand_less" : "expand_more"}</Icon>
                  </ListItemButton>
                  <Collapse in={expanded}>
                    <List disablePadding sx={{ pl: 2 }}>
                      {route.collapse.map((child) => (
                        <ListItemButton
                          key={child.key || child.name}
                          onClick={() => { handleNavigate(child.route || child.href); setMobileOpen(false); }}
                          sx={{
                            borderRadius: 1,
                            mb: 0.25,
                            color: isRouteActive(pathname, child.route) ? "#1e3a5f" : "#64748b",
                            backgroundColor: isRouteActive(pathname, child.route) ? "#eff6ff" : "transparent",
                          }}
                        >
                          <ListItemText primary={child.name} primaryTypographyProps={{ sx: { fontSize: "0.8rem" } }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            }
            return (
              <ListItemButton
                key={route.key}
                onClick={() => { handleNavigate(route.route || route.href); setMobileOpen(false); }}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.5,
                  color: isRouteActive(pathname, route.route) ? "#1e3a5f" : "#475569",
                  fontWeight: isRouteActive(pathname, route.route) ? 700 : 500,
                  backgroundColor: isRouteActive(pathname, route.route) ? "#eff6ff" : "transparent",
                }}
              >
                <ListItemText primary={route.name} primaryTypographyProps={{ sx: { fontSize: "0.875rem", fontWeight: "inherit" } }} />
              </ListItemButton>
            );
          })}
        </List>
        <Divider sx={{ my: 1 }} />
        <List sx={{ px: 1 }}>
          <ListItemButton
            onClick={() => { handleNavigate("/change-password"); setMobileOpen(false); }}
            sx={{ borderRadius: 1.5, color: "#475569" }}
          >
            <Icon sx={{ mr: 1, fontSize: "1.2rem" }}>lock</Icon>
            <ListItemText primary="Change Password" primaryTypographyProps={{ sx: { fontSize: "0.875rem", fontWeight: 500 } }} />
          </ListItemButton>
          <ListItemButton
            onClick={() => { handleSignOut(); setMobileOpen(false); }}
            sx={{ borderRadius: 1.5, color: "#dc2626" }}
          >
            <Icon sx={{ mr: 1, fontSize: "1.2rem" }}>logout</Icon>
            <ListItemText primary="Sign Out" primaryTypographyProps={{ sx: { fontSize: "0.875rem", fontWeight: 600 } }} />
          </ListItemButton>
        </List>
      </Drawer>
    </AppBar>
  );
}

TopMenuBar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  canAccessRoute: PropTypes.func.isRequired,
  topOffset: PropTypes.number,
  leftOffset: PropTypes.number,
  rightOffset: PropTypes.number,
};


